import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../contexts/LanguageContext';

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Configuration EmailJS
      const serviceId = 'service_tsgqwop';
      const templateId = 'template_d7i7tz4';
      const publicKey = '3gHS_JE2pCnmeSdxU';

      // Envoi de l'email via EmailJS
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: form.name,
          from_email: form.email,
          message: form.message,
          to_email: 'contact@pexadia.com',
          reply_to: form.email,
        },
        publicKey
      );

      setIsSubmitting(false);
      setIsSent(true);
      setForm({ name: '', email: '', message: '' });
      setTimeout(() => setIsSent(false), 5000);
    } catch (err) {
      console.error('Erreur lors de l\'envoi:', err);
      setIsSubmitting(false);
      setError('Une erreur est survenue. Veuillez réessayer ou nous contacter directement à contact@pexadia.com');
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-[#050505] relative">
      <div className="pexadia-watermark"></div>
      <div className="max-w-7xl mx-auto relative z-20">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="inline-block px-4 py-1.5 mb-6 border border-[#d4af37]/30 rounded-full bg-[#d4af37]/5">
              <span className="text-[10px] font-black tracking-[0.3em] gold-gradient uppercase">
                {t.contact.badge}
              </span>
            </div>
            <h2 className="text-5xl md:text-7xl font-serif mb-6 italic leading-tight">
              {t.contact.title1} <br />
              <span className="gold-gradient">{t.contact.title2}</span>
            </h2>
            <p className="text-xl text-gray-400 mb-10 leading-relaxed font-light">
              {t.contact.description}
            </p>
            
            <div className="space-y-8">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 glass-card rounded-2xl flex items-center justify-center text-[#d4af37] shrink-0">
                  <i className="fas fa-envelope"></i>
                </div>
                <div>
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">
                    {t.contact.emailLabel}
                  </p>
                  <p className="text-xl font-bold">contact@pexadia.com</p>
                </div>
              </div>
              
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 glass-card rounded-2xl flex items-center justify-center text-[#d4af37] shrink-0">
                  <i className="fas fa-location-dot"></i>
                </div>
                <div>
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">
                    {t.contact.locationLabel}
                  </p>
                  <p className="text-xl font-bold">{t.contact.location}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-[3rem] p-8 md:p-12 relative overflow-hidden border-white/5 shadow-2xl">
            {isSent ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 gold-bg rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-gold/20">
                  <i className="fas fa-check text-black text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-2 uppercase tracking-tighter">
                  {t.contact.form.success}
                </h3>
                <p className="text-gray-400 mb-8 font-light">
                  {t.contact.form.successDesc}
                </p>
                <button
                  onClick={() => setIsSent(false)}
                  className="text-[#d4af37] font-black uppercase tracking-widest text-[10px] border-b border-[#d4af37]/30 pb-1"
                >
                  {t.contact.form.another}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">
                    {t.contact.form.name}
                  </label>
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 focus:outline-none focus:border-[#d4af37] transition-all font-light text-gray-300"
                    placeholder={t.contact.form.placeholderName}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">
                    {t.contact.form.email}
                  </label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 focus:outline-none focus:border-[#d4af37] transition-all font-light text-gray-300"
                    placeholder={t.contact.form.placeholderEmail}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">
                    {t.contact.form.message}
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 focus:outline-none focus:border-[#d4af37] transition-all resize-none font-light text-gray-300"
                    placeholder={t.contact.form.placeholderMessage}
                  />
                </div>
                
                {error && (
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex gap-3 items-center">
                    <i className="fas fa-exclamation-triangle text-red-500"></i>
                    <p className="text-red-400 text-xs leading-tight">{error}</p>
                  </div>
                )}
                <button
                  disabled={isSubmitting}
                  type="submit"
                  className="w-full py-5 gold-bg text-black font-black rounded-2xl disabled:opacity-30 disabled:grayscale transition-all hover:scale-[1.01] active:scale-95 uppercase tracking-widest text-xs shadow-xl shadow-gold/20 flex items-center justify-center gap-3"
                >
                  {isSubmitting ? (
                    <i className="fas fa-circle-notch fa-spin text-xl"></i>
                  ) : (
                    t.contact.form.cta
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;

